import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const PhoneCtx = createContext(null);
export const usePhone = () => useContext(PhoneCtx);

const paramsOf = (call) => {
  const out = {};
  call?.customParameters?.forEach?.((v, k) => (out[k] = v));
  return out;
};

export function PhoneProvider({ children }) {
  const { status } = useApp();
  const ready = status?.twilio?.browser_ready;
  const deviceRef = useRef(null);
  const [deviceState, setDeviceState] = useState("not_configured");
  const [mic, setMic] = useState("unknown");
  const [incoming, setIncoming] = useState(null);
  const [active, setActive] = useState(null);

  const checkMic = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      setMic("ready");
      return true;
    } catch {
      setMic("blocked");
      return false;
    }
  }, []);

  useEffect(() => {
    navigator.permissions?.query({ name: "microphone" }).then((p) => setMic(p.state === "granted" ? "ready" : p.state === "denied" ? "blocked" : "unknown")).catch(() => {});
  }, []);

  const bindCall = useCallback((call, info) => {
    setActive({ call, info, startedAt: null, muted: false, state: "connecting" });
    call.on("accept", () => setActive((a) => a && { ...a, state: "live", startedAt: Date.now() }));
    const end = () => setActive(null);
    call.on("disconnect", end);
    call.on("cancel", end);
    call.on("reject", end);
    call.on("error", (e) => {
      toast.error(e.message);
      end();
    });
  }, []);

  useEffect(() => {
    if (!ready) {
      setDeviceState("not_configured");
      return undefined;
    }
    let dev;
    let cancelled = false;
    (async () => {
      try {
        setDeviceState("registering");
        const { data } = await api.get("/voice/token");
        if (cancelled) return;
        dev = new Device(data.token, { closeProtection: true, codecPreferences: ["opus", "pcmu"] });
        dev.on("registered", () => setDeviceState("ready"));
        dev.on("unregistered", () => setDeviceState("offline"));
        dev.on("error", (e) => setDeviceState(e?.code === 31208 ? "mic_blocked" : "error"));
        dev.on("tokenWillExpire", async () => {
          const r = await api.get("/voice/token");
          dev.updateToken(r.data.token);
        });
        dev.on("incoming", (call) => {
          const p = paramsOf(call);
          setIncoming({ call, info: { name: p.contact_name || call.parameters.From, contactId: p.contact_id, callId: p.call_id, direction: p.direction || "queue" } });
          call.on("cancel", () => setIncoming(null));
          call.on("disconnect", () => setIncoming(null));
        });
        deviceRef.current = dev;
        await dev.register();
      } catch (e) {
        if (!cancelled) setDeviceState("error");
      }
    })();
    return () => {
      cancelled = true;
      dev?.destroy();
      deviceRef.current = null;
    };
  }, [ready]);

  useEffect(() => {
    const beat = () => api.post("/me/heartbeat", { device_ready: deviceState === "ready" }).catch(() => {});
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [deviceState]);

  const connect = useCallback(async (params, info) => {
    const dev = deviceRef.current;
    if (!dev || deviceState !== "ready") throw new Error("Browser phone is not ready");
    if (!(await checkMic())) throw new Error("Microphone access is blocked");
    const call = await dev.connect({ params });
    bindCall(call, info);
    return call;
  }, [deviceState, bindCall, checkMic]);

  const answer = useCallback(async () => {
    if (!incoming) return;
    if (!(await checkMic())) return toast.error("Allow microphone access to answer");
    incoming.call.accept();
    bindCall(incoming.call, incoming.info);
    setIncoming(null);
  }, [incoming, bindCall, checkMic]);

  const reject = useCallback(() => {
    incoming?.call.reject();
    setIncoming(null);
  }, [incoming]);

  const hangup = useCallback(() => active?.call.disconnect(), [active]);
  const toggleMute = useCallback(() => {
    if (!active) return;
    active.call.mute(!active.muted);
    setActive({ ...active, muted: !active.muted });
  }, [active]);

  const startBrowserCall = useCallback(async (callDoc) => {
    try {
      await connect({ CallId: callDoc.id }, { name: callDoc.contact_name, number: callDoc.to_number, callId: callDoc.id, contactId: callDoc.contact_id });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [connect]);

  const takeover = useCallback(async (callId, name) => {
    try {
      if (deviceState !== "ready") throw new Error("Browser phone is not ready — takeover needs your microphone");
      await api.post(`/ai-calls/${callId}/takeover`);
      await connect({ Takeover: callId }, { name, callId, takeover: true });
      toast.success("You are now on the call");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [connect, deviceState]);

  return (
    <PhoneCtx.Provider value={{ deviceState, mic, checkMic, incoming, active, answer, reject, hangup, toggleMute, startBrowserCall, takeover }}>
      {children}
    </PhoneCtx.Provider>
  );
}
